import type { ThemeTokens } from "@klarity/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Spinner } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { BlockDiff, DocumentFull } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";
import { ThemeCanvas } from "../canvas/ThemeCanvas";
import { useDocumentHost } from "../editing/useDocumentHost";
import { KlarityEditor } from "../editor/KlarityEditor";
import { blockLabel, blockSummary } from "./diff";

function DiffRow({ d, version }: { d: BlockDiff; version: number }) {
  if (d.kind === "changed") {
    return (
      <li className="rounded-md border-l-4 border-orange bg-orange-wash/60 px-3 py-2">
        <div className="text-xs font-medium text-orange-ink">Changed · {blockLabel(d.after)}</div>
        <p className="text-ink-2 line-through decoration-ink-3">{blockSummary(d.before)}</p>
        <p className="text-ink">{blockSummary(d.after)}</p>
      </li>
    );
  }
  const style = {
    same: "border-transparent text-ink-3",
    added: "border-green bg-green-wash/60 text-ink",
    removed: "border-danger bg-danger-wash/60 text-ink",
  }[d.kind];
  const tag = { same: null, added: "Only in the current version", removed: `Only in version ${version}` }[d.kind];
  return (
    <li className={clsx("rounded-md border-l-4 px-3 py-1.5", style)}>
      {tag ? <div className="text-xs font-medium">{tag}</div> : null}
      <p>
        <span className="mr-2 text-xs text-ink-3">{blockLabel(d.block)}</span>
        {blockSummary(d.block)}
      </p>
    </li>
  );
}

export function RevisionView({
  doc,
  tokens,
  version,
  onClose,
}: {
  doc: DocumentFull;
  tokens: ThemeTokens;
  version: number;
  onClose: () => void;
}) {
  const me = useMe();
  const { host } = useDocumentHost(doc.id, tokens, doc.meta);
  const queryClient = useQueryClient();
  const { confirm, element } = useConfirm();
  const [tab, setTab] = useState<"changes" | "full">("changes");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const {
    data: rev,
    isLoading,
    isError: revFailed,
  } = useQuery({
    queryKey: qk.revision(doc.id, version),
    queryFn: () => api.revisions.get(doc.id, version),
  });
  // Server diff "revision → current": added = only in the current version, removed = only in this revision.
  const {
    data: diff = [],
    isError: diffFailed,
    isSuccess: diffLoaded,
  } = useQuery({
    queryKey: qk.diff(doc.id, version),
    queryFn: () => api.revisions.diff(doc.id, version),
  });
  const restore = useMutation({
    mutationFn: () => api.revisions.restore(doc.id, version, doc.version),
    onSuccess: async (r) => {
      toast(`Version ${version} restored as version ${r.version}.`, "success");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["doc"] }),
        queryClient.invalidateQueries({ queryKey: qk.revisions(doc.id) }),
      ]);
      if (mounted.current) onClose();
    },
    onError: (e) => {
      toast(`Restore failed: ${e.message}`, "error");
      // A version conflict means the loaded document is stale; refresh it so a retry sends the current version.
      if (e instanceof ApiError && e.status === 409) void queryClient.invalidateQueries({ queryKey: ["doc"] });
    },
  });

  if (revFailed) {
    return (
      <div role="alert" className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 text-sm">
        <span className="flex-1 text-danger">Version {version} could not be loaded.</span>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }
  if (isLoading || !rev) return <Spinner label={`Loading version ${version}…`} />;
  const changes = diff.filter((d) => d.kind !== "same").length;

  return (
    <section aria-label={`Version ${version}`} className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
        <div className="flex-1">
          <div className="font-medium text-ink">Version {version}, read-only</div>
          <div className="text-sm text-ink-2">
            {rev.author?.name ?? "Unknown"} · {formatDateTime(rev.createdAt)}
            {diffLoaded ? ` · ${changes} block${changes === 1 ? "" : "s"} differ from the current version` : null}
          </div>
        </div>
        {can(me, "edit") ? (
          <Button
            onClick={async () => {
              const ok = await confirm({
                title: `Restore version ${version}?`,
                body: "The current content stays in the history. Restoring saves this version as a new version.",
                confirmLabel: "Restore",
              });
              if (ok && mounted.current) restore.mutate();
            }}
            disabled={restore.isPending}
          >
            <RotateCcw size={15} /> Restore this version
          </Button>
        ) : null}
        <Button variant="ghost" aria-label="Close version" onClick={onClose}>
          <X size={16} />
        </Button>
      </div>
      <div role="tablist" aria-label="Version view" className="flex gap-1">
        {(["changes", "full"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={clsx(
              "rounded-md px-3 py-1 text-sm",
              tab === t ? "bg-green-wash text-green-ink" : "text-ink-2 hover:bg-surface",
            )}
          >
            {t === "changes" ? "Changes" : "Full text"}
          </button>
        ))}
      </div>
      {tab === "changes" && diffFailed ? (
        <p role="alert" className="text-sm text-danger">
          The comparison could not be loaded
        </p>
      ) : tab === "changes" ? (
        <ol role="tabpanel" aria-label="Changes" className="grid gap-1.5 text-sm">
          {diff.map((d, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: diff rows have no identity beyond their position
            <DiffRow key={i} d={d} version={version} />
          ))}
        </ol>
      ) : (
        <div role="tabpanel" aria-label="Full text">
          <ThemeCanvas
            tokens={tokens}
            fileUrl={host.attachmentUrl}
            doc={{ kind: doc.kind, title: rev.title, meta: rev.meta }}
          >
            <KlarityEditor key={version} content={rev.content} mode="read" host={host} />
          </ThemeCanvas>
        </div>
      )}
      {element}
    </section>
  );
}
